/**
 * Dịp chụp của đơn + số lời khách chưa sửa (QĐ 0480) — một cửa cho năm chỗ
 * nhập: tạo đơn, chi tiết đơn, khung chat, gọi xác nhận, thẻ ca ekip.
 *
 *   GET /orders/{id}/occasion · PUT /orders/{id}/occasion
 *   GET /orders/occasions?ids= · GET /orders/customer-notes-pending?ids=
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { customerKeys } from "@/features/customers/api";

export type OrderOccasion = components["schemas"]["OrderOccasion"];
export type OrderOccasionKind = components["schemas"]["OrderOccasionKind"];
export type OrderOccasionInput = components["schemas"]["OrderOccasionInput"];

export const occasionKeys = {
  all: ["order-occasions"] as const,
  one: (orderId: string) => ["order-occasions", "one", orderId] as const,
  many: (ids: string) => ["order-occasions", "many", ids] as const,
  pendingNotes: (ids: string) => ["order-occasions", "pending-notes", ids] as const,
};

/** Tên hiện màn của từng dịp — thứ tự là thứ tự hàng nút chọn. */
export const OCCASION_KINDS: ReadonlyArray<{ kind: OrderOccasionKind; label: string; icon: string }> = [
  { kind: "birthday", label: "Sinh nhật", icon: "🎂" },
  { kind: "wedding_anniversary", label: "Kỷ niệm cưới", icon: "💍" },
  { kind: "graduation", label: "Tốt nghiệp", icon: "🎓" },
  { kind: "longevity", label: "Mừng thọ", icon: "🏮" },
  { kind: "other", label: "Dịp khác", icon: "✨" },
];

export function occasionLabel(kind: OrderOccasionKind): { label: string; icon: string } {
  return OCCASION_KINDS.find((k) => k.kind === kind) ?? { label: "Dịp khác", icon: "✨" };
}

/** Mã đơn gọn, xếp, bỏ trùng — làm khoá truy vấn ổn định cho một trang danh sách. */
function idKey(ids: readonly string[]): string {
  return [...new Set(ids.filter(Boolean))].sort().join(",");
}

export function useOrderOccasion(orderId: string, enabled = true) {
  return useQuery({
    queryKey: occasionKeys.one(orderId),
    enabled: enabled && orderId.length > 0,
    queryFn: async (): Promise<OrderOccasion | null> => {
      const { data, error } = await api.GET("/orders/{id}/occasion", { params: { path: { id: orderId } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được dịp chụp của đơn"));
      return data.occasion ?? null;
    },
    staleTime: 60_000,
  });
}

/** Dịp chụp của cả trang danh sách, một lượt gọi. Đơn chưa ghi dịp vắng mặt. */
export function useOrderOccasions(ids: readonly string[], enabled = true) {
  const key = idKey(ids);
  return useQuery({
    queryKey: occasionKeys.many(key),
    enabled: enabled && key.length > 0,
    queryFn: async (): Promise<Record<string, OrderOccasion>> => {
      const { data, error } = await api.GET("/orders/occasions", { params: { query: { ids: key } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được dịp chụp của danh sách"));
      return data;
    },
    staleTime: 60_000,
  });
}

/** Số lời khách từng ảnh chưa sửa của cả trang danh sách (thẻ CSKH, Câu 3a). */
export function usePendingCustomerNotes(ids: readonly string[], enabled = true) {
  const key = idKey(ids);
  return useQuery({
    queryKey: occasionKeys.pendingNotes(key),
    enabled: enabled && key.length > 0,
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await api.GET("/orders/customer-notes-pending", { params: { query: { ids: key } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đếm được lời khách chưa sửa"));
      return data;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function usePutOrderOccasion(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: OrderOccasionInput): Promise<OrderOccasion | null> => {
      const { data, error } = await api.PUT("/orders/{id}/occasion", {
        params: { path: { id: orderId } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được dịp chụp"));
      return data.occasion ?? null;
    },
    onSuccess: (saved, input) => {
      qc.setQueryData(occasionKeys.one(orderId), saved);
      void qc.invalidateQueries({ queryKey: occasionKeys.all });
      // Sinh nhật của người đặt ghi thẳng hồ sơ khách (7a) — làm tươi màn khách.
      if (input.kind === "birthday" && input.person === "self") {
        void qc.invalidateQueries({ queryKey: customerKeys.all });
      }
    },
  });
}

/** Lưu dịp cho một đơn vừa tạo (form tạo đơn) — không qua hook. */
export async function putOrderOccasion(orderId: string, input: OrderOccasionInput): Promise<void> {
  const { error } = await api.PUT("/orders/{id}/occasion", { params: { path: { id: orderId } }, body: input });
  if (error) throw new Error(serverErrorMessage(error, "Không lưu được dịp chụp"));
}
